import { useLiveQuery } from 'dexie-react-hooks';
import { ArrowRight, ChevronRight, ClipboardList, Menu, Minus, PauseCircle, Pencil, Percent, Plus, Trash2, UserPlus, X } from 'lucide-react';
import { formatMoney, formatQty, lineNet } from '@sense/shared';
import type { CartLine } from '../lib/cart';
import { useCustomer } from '../lib/customers';
import { db } from '../lib/db';
import type { Priced } from '../lib/pricing';
import { Sheet } from './ui';
import { useT, tr } from '../lib/i18n';

/**
 * Phone billing, laid out like Sense Fresh: title bar with Hold / Held / Clear, the bill as cards,
 * a summary card and a floating Charge bar above the tabs.
 */
export function MobileBillHeader({ onMenu, onHold, onHeld, onClear, count }: { onMenu(): void; onHold(): void; onHeld(): void; onClear(): void; count: number }) {
  const hasLines = count > 0;
  const held = useLiveQuery(() => db.held.count(), []);
  const tx = useT();
  return (
    <div className="flex h-16 items-center gap-2 bg-paper px-3 lg:hidden">
      <button aria-label="Open menu" onClick={onMenu} className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full"><Menu size={22} /></button>
      <div className="min-w-0 flex-1"><h1 className="truncate text-xl font-bold">{tx('Billing')}</h1>{hasLines && <div className="num truncate text-xs text-ink-3">{tx(count === 1 ? '1 item on this bill' : '{n} items on this bill', { n: count })}</div>}</div>
      {/* Icon-only when the label is long (Tamil, Hindi), so the title keeps its room. */}
      <button onClick={onHold} disabled={!hasLines} aria-label={tx('Hold')} title={tx('Hold')} className="flex h-9 shrink-0 items-center gap-1.5 rounded-full border border-line bg-white px-3 text-xs font-semibold disabled:opacity-50">
        <PauseCircle size={16} />{tx('Hold').length <= 5 && <span>{tx('Hold')}</span>}
      </button>
      <button aria-label={held ? `Held bills (${held})` : 'Held bills'} onClick={onHeld} className="relative flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-line bg-white">
        <ClipboardList size={20} />
        {held ? <span className="num absolute -top-1 -right-1 min-w-5 rounded-full bg-forest-700 px-1 text-center text-2xs leading-5 font-semibold text-white">{held}</span> : null}
      </button>
      <button aria-label="Clear bill" onClick={onClear} disabled={!hasLines} className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-line bg-white text-danger disabled:opacity-50">
        <Trash2 size={18} />
      </button>
    </div>
  );
}

/** "Add Customer" dashed card, or the chosen customer with their khata balance. */
export function CustomerCard({ customer, onPick, onClear }: { customer: { id: string; name: string; phone: string } | null; onPick(): void; onClear(): void }) {
  const full = useCustomer(customer?.id);
  const tx = useT();
  if (!customer) {
    return (
      <button onClick={onPick} aria-label="Add customer" className="flex h-14 w-full items-center gap-3 rounded-card border-2 border-dashed border-line px-4 text-left text-sm font-semibold text-muted lg:hidden">
        <UserPlus size={22} className="text-ink" /><span className="flex-1">{tx('Add Customer')}</span><ChevronRight size={18} />
      </button>
    );
  }
  return (
    <div className="flex items-center gap-3 rounded-card bg-white px-4 py-3 shadow-card lg:hidden">
      <button onClick={onPick} aria-label={`Customer ${customer.name}`} className="flex min-w-0 flex-1 items-center gap-3 text-left">
        <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-t-blue text-xs font-semibold text-t-blue-fg">{customer.name.slice(0, 2).toUpperCase()}</span>
        <span className="min-w-0">
          <span className="block truncate text-sm font-semibold">{customer.name}</span>
          <span className="num block truncate text-xs text-muted">{customer.phone}{full && full.balance > 0 ? ` · ${tx('owes {a}', { a: formatMoney(full.balance) })}` : ''}</span>
        </span>
      </button>
      <button aria-label="Remove customer" onClick={onClear} className="flex h-9 w-9 items-center justify-center rounded-full text-muted hover:bg-paper"><X size={18} /></button>
    </div>
  );
}

const unitLabel = (u: CartLine['unit']) => (u === 'pc' ? 'pc' : u === 'pack' ? 'pack' : u);

/** One bill line as a card: name, price chip and delete, then − qty + and the line total. */
export function LineCard({ l, offer, offerName, onDec, onInc, onQty, onPrice, onOptions, onRemove }: {
  l: CartLine; offer: number; offerName?: string; onDec(): void; onInc(): void; onQty(): void; onPrice(): void; onOptions(): void; onRemove(): void;
}) {
  const total = lineNet([l.pid, l.qty, l.price, l.disc + offer, l.tax_bp]);
  const tx = useT();
  const priceChip = (
    <button onClick={onPrice} title={tr('Change price')} className="num flex h-8 shrink-0 items-center gap-1 rounded-full bg-surface-2 px-2.5 text-xs font-medium whitespace-nowrap">
      {formatMoney(l.price, { whole: l.price % 100 === 0 })} / {unitLabel(l.unit)}<Pencil size={12} className="text-ink-3" />
    </button>
  );
  // Compact card: name and line total, then price · − qty + · remove — two short rows.
  return (
    <div className="flex flex-col gap-2 rounded-card bg-white px-3 py-2.5 shadow-card">
      <div className="flex items-start gap-3">
        <button onClick={onOptions} className="line-clamp-2 min-w-0 flex-1 text-left text-sm leading-snug font-semibold" aria-label={`Options for ${l.name}`}>{l.name}</button>
        <span className="num shrink-0 text-base font-bold">{formatMoney(total)}</span>
      </div>
      {(offer > 0 || l.disc > 0 || l.batch) && (
        <div className="-mt-1 flex flex-wrap items-center gap-x-3 gap-y-1">
          {offer > 0 && <span className="text-xs font-semibold text-t-amber-fg">{offerName ?? tx('Offer')} −{formatMoney(offer)}</span>}
          {l.disc > 0 && <span className="text-xs font-semibold text-t-amber-fg">{tx('Discount')} −{formatMoney(l.disc)}</span>}
          {l.batch && <span className="text-xs text-ink-3">{tx('Batch')} {l.batch}</span>}
        </div>
      )}
      <div className="flex items-center gap-1.5">
        {priceChip}
        <span className="flex-1" />
        <button aria-label={`Decrease ${l.name}`} onClick={onDec} className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-t-red text-danger"><Minus size={18} /></button>
        <button onClick={onQty} aria-label={`Quantity of ${l.name}: ${formatQty(l.qty, l.unit)}`} className="num min-w-9 px-1 text-center text-sm font-bold">{formatQty(l.qty, l.unit)}</button>
        <button aria-label={`Increase ${l.name}`} onClick={onInc} className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-forest-700 text-white"><Plus size={18} /></button>
        <button aria-label={`Remove ${l.name}`} onClick={onRemove} className="ml-1 flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-danger hover:bg-t-red"><Trash2 size={17} /></button>
      </div>
    </div>
  );
}

/** Floating action bar (56 px) above the tabs: item count, total and Charge. */
export function ChargeBar({ priced, count, onCharge }: { priced: Priced; count: number; onCharge(): void }) {
  const tx = useT();
  return (
    <div className="fixed inset-x-0 bottom-[var(--tabbar-h,64px)] z-20 px-4 pb-3 lg:hidden">
      <button onClick={onCharge} aria-label={`Pay ${formatMoney(priced.totals.total)}`}
        className="flex h-14 w-full items-center gap-3 rounded-2xl bg-forest-700 px-5 text-base font-semibold text-white shadow-lift transition active:scale-[.98] active:bg-forest-800">
        <span className="num min-w-0 flex-1 truncate text-left">{tx(count === 1 ? '1 item' : '{n} items', { n: count })} · {formatMoney(priced.totals.total)}</span>
        <span className="flex items-center gap-1.5">{tx('Charge')}<ArrowRight size={20} /></span>
      </button>
    </div>
  );
}

/** Summary card under the lines: subtotal, offers, discount, GST, round off and the total. */
export function SummaryCard({ priced, onDiscount }: { priced: Priced; onDiscount(): void }) {
  const t = priced.totals;
  const tx = useT();
  const row = (k: string, v: string, cls = '') => <div className={`flex justify-between gap-3 ${cls}`}><span>{tx(k)}</span><span className="num">{v}</span></div>;
  return (
    <div className="flex flex-col gap-2.5 rounded-card bg-white p-5 text-sm shadow-card lg:hidden">
      {row(tr('Subtotal'), formatMoney(t.subtotal))}
      {priced.applied.map((a) => <div key={a.offerId}>{row(a.name, `−${formatMoney(a.amount)}`, 'font-semibold text-t-amber-fg')}</div>)}
      <div className="flex items-center justify-between gap-3">
        <button onClick={onDiscount} className="flex items-center gap-1.5 font-semibold text-primary"><Percent size={16} />{tx(priced.manual ? 'Change discount' : 'Add discount')}</button>
        <span className="num">{priced.manual ? `−${formatMoney(priced.manual)}` : '—'}</span>
      </div>
      {row(tr('GST (included)'), formatMoney(t.tax), 'text-muted')}
      {t.round !== 0 && row(tr('Round off'), `${t.round > 0 ? '+' : ''}${formatMoney(t.round)}`, 'text-muted')}
      <div className="mt-1 flex items-baseline justify-between border-t border-line pt-3"><span className="text-base font-bold">{tx('Total')}</span><span className="num text-2xl font-bold">{formatMoney(t.total)}</span></div>
    </div>
  );
}

/** Tapping an item's name: everything you can do with that line. */
export function LineOptions({ line, onClose, onQty, onPrice, onDiscount, onRemove }: { line: CartLine | null; onClose(): void; onQty(): void; onPrice(): void; onDiscount(): void; onRemove(): void }) {
  const tx = useT();
  const item = (icon: typeof Pencil, label: string, run: () => void, danger = false) => {
    const Icon = icon;
    return (
      <button onClick={run} className={`flex h-14 w-full items-center gap-3 rounded-card bg-white px-4 text-left text-base font-semibold shadow-card ${danger ? 'text-danger' : ''}`}>
        <Icon size={20} />{label}<ChevronRight size={18} className="ml-auto text-muted" />
      </button>
    );
  };
  return (
    <Sheet open={!!line} onClose={onClose} title={line?.name ?? ''}>
      {line && (
        <div className="flex flex-col gap-2">
          {item(Plus, `${tx('Quantity')} · ${formatQty(line.qty, line.unit)}`, onQty)}
          {item(Pencil, `${tx('Price')} · ${formatMoney(line.price)} / ${unitLabel(line.unit)}`, onPrice)}
          {item(Percent, line.disc ? `${tx('Discount')} · −${formatMoney(line.disc)}` : tx('Discount'), onDiscount)}
          {item(Trash2, tx('Remove from bill'), onRemove, true)}
        </div>
      )}
    </Sheet>
  );
}
